import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';

import styled, { keyframes } from 'styled-components';
import 'react-pdf/dist/Page/AnnotationLayer.css';

const BookWrapper = styled.div`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
`;

const FlipPage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ currentPage, index }) => ((currentPage > index) ? 'translate(-100%, 0)' : 'translate(0, 0)')};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 2px solid rgba(0,0,0,0.08);
  border-top-color: rgba(0,0,0,0.3);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;


pdfjs.GlobalWorkerOptions.workerSrc = "https://pdfdeck.com/static/website/js/pdfworker.js";

const Flipbook = ({ documentUrl, dlEnabled, showBranding, brandingLink }) => {
    const [numPages, setNumPages] = useState(null);
    const [pages, setPages] = useState([]);
    const [width, setWidth] = useState(500);
    const [height, setHeight] = useState(312);
    const [aspect, setAspect] = useState(0.70921985815);
    const [loading, setLoading] = useState(true);
    const [maxHeight, setMaxHeight] = useState(846.000000007);
    const [maxWidth, setMaxWidth] = useState(800);
    const isDlEnabled = (dlEnabled === 'true');
    const doShowBranding = (showBranding === 'true');

    const [currentPage, setCurrentPage] = useState(1);
    const [startX, setStartX] = useState(null);
    const [chromeVisible, setChromeVisible] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isNarrow, setIsNarrow] = useState(false);

    const currentPageRef = useRef(currentPage);
    const numPagesRef = useRef(numPages);
    const fullscreenRef = useRef(null);
    const hideTimerRef = useRef(null);

    // --- Navigation -----------------------------------------------------

    const nextPage = () => setCurrentPage((p) => (p < numPagesRef.current ? p + 1 : p));
    const prev = () => setCurrentPage((p) => (p > 1 ? p - 1 : p));
    const goToPage = (pageNum) => setCurrentPage(pageNum);
    const specialNext = (curr) => setCurrentPage(curr + 1);
    const specialPrev = (curr) => setCurrentPage(curr - 1);
    const goToFirst = () => setCurrentPage(1);
    const goToLast = () => setCurrentPage(numPagesRef.current || 1);

    // --- Fullscreen -----------------------------------------------------

    const toggleFullscreen = () => {
        const el = fullscreenRef.current;
        if (!el) return;
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        } else {
            if (el.requestFullscreen) el.requestFullscreen();
            else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        }
    };

    useEffect(() => {
        const onChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
        };
        document.addEventListener('fullscreenchange', onChange);
        document.addEventListener('webkitfullscreenchange', onChange);
        return () => {
            document.removeEventListener('fullscreenchange', onChange);
            document.removeEventListener('webkitfullscreenchange', onChange);
        };
    }, []);

    // --- Touch swipe ----------------------------------------------------

    const handleTouchStart = (e) => {
        setStartX(e.touches[0].clientX);
    };

    const handleTouchEnd = (e) => {
        if (!startX) return;
        const endX = e.changedTouches[0].clientX;
        const diffX = endX - startX;
        const threshold = 100;
        if (Math.abs(diffX) > threshold) {
            if (diffX > 0) {
                if (currentPage > 1) prev();
            } else {
                if (numPages > currentPage) nextPage();
            }
        }
        setStartX(null);
    };

    // --- Keyboard -------------------------------------------------------

    const handleKeyDown = (event) => {
        const key = event.key;
        const curr = currentPageRef.current;
        const total = numPagesRef.current;

        if (key === 'ArrowLeft' || key === 'PageUp' || key === 'Backspace') {
            if (curr > 1) {
                event.preventDefault();
                specialPrev(curr);
                revealChrome();
            }
        } else if (key === 'ArrowRight' || key === 'PageDown' || key === ' ') {
            if (total && curr < total) {
                event.preventDefault();
                specialNext(curr);
                revealChrome();
            }
        } else if (key === 'Home') {
            event.preventDefault();
            goToFirst();
            revealChrome();
        } else if (key === 'End') {
            event.preventDefault();
            goToLast();
            revealChrome();
        } else if (key === 'f' || key === 'F') {
            event.preventDefault();
            toggleFullscreen();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // --- Auto-hide chrome -----------------------------------------------

    const revealChrome = () => {
        setChromeVisible(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setChromeVisible(false), 3000);
    };

    useEffect(() => {
        const onMove = () => revealChrome();
        const onTouch = () => revealChrome();
        document.addEventListener('mousemove', onMove);
        document.addEventListener('touchstart', onTouch, { passive: true });
        revealChrome();
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('touchstart', onTouch);
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        };
    }, []);

    // --- Click-to-advance ----------------------------------------------

    const handleBackgroundClick = (e) => {
        // Skip clicks on chrome elements and PDF internal links.
        if (e.target.closest('[data-presentation-chrome]')) return;
        if (e.target.closest('a')) return;
        const midpoint = window.innerWidth / 2;
        if (e.clientX > midpoint) {
            if (currentPageRef.current < numPagesRef.current) specialNext(currentPageRef.current);
        } else {
            if (currentPageRef.current > 1) specialPrev(currentPageRef.current);
        }
    };

    // --- Resize ---------------------------------------------------------

    const debounce = (func, delay) => {
        let timeoutId;
        return function (...args) {
          const context = this;
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            func.apply(context, args);
          }, delay);
        };
    };

    function handleResize() {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Less vertical padding on short viewports so the slide doesn't get crushed.
        const verticalPad = windowHeight < 600 ? 60 : 120;
        const horizontalPad = windowWidth < 600 ? 24 : 64;

        let maxWidthToSet = Math.min(windowWidth - horizontalPad, 1400);
        let maxHeightToSet = Math.min(windowHeight - verticalPad, maxWidthToSet / aspect);

        maxWidthToSet = Math.min(maxWidthToSet, maxHeightToSet * aspect);

        setMaxWidth(maxWidthToSet);
        setMaxHeight(maxHeightToSet);
    }

    const debouncedResize = debounce(handleResize, 50);

    useEffect(() => {
        currentPageRef.current = currentPage;
        numPagesRef.current = numPages;
    }, [currentPage, numPages]);


    useEffect(() => {
        handleResize();
        window.addEventListener('resize', debouncedResize);
        window.addEventListener('orientationchange', debouncedResize);
        window.addEventListener('gesturechange', debouncedResize);
        return () => {
            window.removeEventListener('resize', debouncedResize);
            window.removeEventListener('orientationchange', debouncedResize)
            window.removeEventListener('gesturechange', debouncedResize)
        };
    }, [aspect, loading]);

    useEffect(() => {
        const checkWidth = () => setIsNarrow(window.innerWidth < 640);
        checkWidth();
        window.addEventListener('resize', checkWidth);
        return () => window.removeEventListener('resize', checkWidth);
    }, []);

    const onDocumentLoadSuccess = async (pdf) => {
        const page = await pdf.getPage(1);
        setWidth(page.view[2]);
        setHeight(page.view[3]);
        const aspectToSet = page.view[2] / page.view[3];
        setAspect(aspectToSet);
        setNumPages(pdf.numPages);
        setMaxHeight(maxWidth / aspectToSet);
        var pagesToSet = [];
        for (var i = 1; i <= pdf.numPages; i++) {
            pagesToSet.push(<div style={{ background: "white" }}></div>);
        }
        setPages(pagesToSet);
        setLoading(false);
    };

    function onItemClick({ pageNumber: itemPageNumber }) {
        goToPage(itemPageNumber);
    };

    const atStart = !currentPage || currentPage <= 1;
    const atEnd = !numPages || currentPage >= numPages;
    const progressPct = (numPages && currentPage) ? (currentPage / numPages) * 100 : 0;

    const chromeFont = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif";
    const chromeStyle = {
        opacity: chromeVisible ? 1 : 0,
        pointerEvents: chromeVisible ? 'auto' : 'none',
        transition: 'opacity 0.25s ease',
    };

    return (
        <div
            ref={fullscreenRef}
            onClick={handleBackgroundClick}
            style={{
                background: "#0c0a09",
                minHeight: "100vh",
                width: "100vw",
                position: "relative",
                overflow: "hidden",
                color: "rgba(255,255,255,0.9)",
                fontFamily: chromeFont,
                cursor: chromeVisible ? 'auto' : 'none',
            }}
        >
            {/* Progress bar */}
            {numPages && (
                <div
                    data-presentation-chrome="true"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "rgba(255,255,255,0.06)",
                        zIndex: 10,
                        ...chromeStyle,
                    }}
                >
                    <div style={{
                        height: "100%",
                        width: `${progressPct}%`,
                        background: "rgba(255,255,255,0.85)",
                        transition: "width 0.25s ease",
                    }} />
                </div>
            )}

            {/* Centered slide */}
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100vw",
                height: "100vh",
            }}>
                <div style={{
                    boxShadow: "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)",
                    overflow: "hidden",
                    background: "white",
                    width: maxWidth,
                    height: maxHeight,
                }}>
                    <Document
                        file={documentUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onItemClick={onItemClick}
                        externalLinkTarget="_blank"
                        loading={
                            <div style={{
                                width: maxWidth,
                                height: maxHeight,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "white",
                            }}>
                                <Spinner />
                            </div>
                        }
                    >
                        {!loading &&
                            <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                                <BookWrapper width={maxWidth} height={maxHeight}>
                                {pages.map((page, index) => (
                                    <React.Fragment key={index}>
                                    {Math.abs(currentPage - (pages.length - index)) <= 2 &&
                                        <FlipPage currentPage={currentPage} index={pages.length - index}>
                                            <Page style={{background: "white"}} width={maxWidth} height={maxHeight} pageNumber={numPages - index} renderTextLayer={false} />
                                        </FlipPage>
                                    }
                                    </React.Fragment>
                                ))}
                                </BookWrapper>
                            </div>
                        }
                    </Document>
                </div>
            </div>

            {/* Floating navigation pill */}
            {(currentPage && numPages && numPages > 1) && (
                <div
                    data-presentation-chrome="true"
                    style={{
                        position: "fixed",
                        bottom: "28px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        padding: "6px 8px",
                        background: "rgba(20,18,16,0.72)",
                        backdropFilter: "blur(14px) saturate(140%)",
                        WebkitBackdropFilter: "blur(14px) saturate(140%)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "9999px",
                        zIndex: 10,
                        ...chromeStyle,
                    }}
                >
                    <NavButton
                        onClick={atStart ? null : prev}
                        disabled={atStart}
                        title="Previous page (←)"
                        path="M15.75 19.5 8.25 12l7.5-7.5"
                    />
                    <div style={{
                        fontSize: "13px",
                        fontWeight: 500,
                        fontVariantNumeric: "tabular-nums",
                        padding: "0 10px",
                        color: "rgba(255,255,255,0.92)",
                        letterSpacing: "0.01em",
                    }}>
                        {currentPage}<span style={{color: "rgba(255,255,255,0.38)", margin: "0 6px"}}>/</span>{numPages}
                    </div>
                    <NavButton
                        onClick={atEnd ? null : nextPage}
                        disabled={atEnd}
                        title="Next page (→)"
                        path="m8.25 4.5 7.5 7.5-7.5 7.5"
                    />
                </div>
            )}

            {/* Top-right: functional chrome (fades with inactivity) */}
            <div
                data-presentation-chrome="true"
                style={{
                    position: "fixed",
                    top: "16px",
                    right: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    zIndex: 10,
                    ...chromeStyle,
                }}
            >
                <CornerIconButton
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "Exit fullscreen (F)" : "Enter fullscreen (F)"}
                    path={isFullscreen
                        ? "M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"
                        : "M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"}
                />
                {isDlEnabled && (
                    <CornerIconLink
                        href={documentUrl}
                        download="presentation.pdf"
                        title="Download PDF"
                        path="m9 13.5 3 3m0 0 3-3m-3 3v-6m1.06-4.19-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
                    />
                )}
            </div>

            {/* Branding — paid feature to remove, so always visible (no fade) */}
            {doShowBranding && (
                <div
                    data-presentation-chrome="true"
                    style={{
                        position: "fixed",
                        bottom: "32px",
                        right: "28px",
                        zIndex: 10,
                    }}
                >
                    <BrandingLink href={brandingLink} compact={isNarrow} />
                </div>
            )}
        </div>
    );
};

// --- Chrome sub-components ---

const NavButton = ({ onClick, disabled, title, path }) => {
    const [hover, setHover] = useState(false);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }}
            disabled={disabled}
            title={title}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                cursor: disabled ? "default" : "pointer",
                background: (!disabled && hover) ? "rgba(255,255,255,0.12)" : "transparent",
                border: "none",
                padding: "10px 12px",
                color: "rgba(255,255,255,0.92)",
                borderRadius: "9999px",
                opacity: disabled ? 0.28 : 1,
                transition: "background 0.15s ease, opacity 0.15s ease",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{height: "14px", width: "14px"}}>
                <path strokeLinecap="round" strokeLinejoin="round" d={path} />
            </svg>
        </button>
    );
};

const CornerIconButton = ({ onClick, title, path }) => {
    const [hover, setHover] = useState(false);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            title={title}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                cursor: "pointer",
                background: "transparent",
                border: "none",
                padding: "8px",
                color: hover ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.55)",
                transition: "color 0.15s ease",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" style={{height: "18px", width: "18px"}}>
                <path strokeLinecap="round" strokeLinejoin="round" d={path} />
            </svg>
        </button>
    );
};

const CornerIconLink = ({ href, download, title, path }) => {
    const [hover, setHover] = useState(false);
    return (
        <a
            href={href}
            download={download}
            title={title}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px",
                color: hover ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.55)",
                transition: "color 0.15s ease",
                textDecoration: "none",
            }}
        >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" style={{height: "18px", width: "18px"}}>
                <path strokeLinecap="round" strokeLinejoin="round" d={path} />
            </svg>
        </a>
    );
};

const BrandingLink = ({ href, compact }) => {
    const [hover, setHover] = useState(false);
    return (
        <a
            href={href}
            target="_blank"
            rel="nofollow noopener"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                fontFamily: "ui-serif, Georgia, Cambria, 'Times New Roman', serif",
                fontStyle: "italic",
                fontSize: "13px",
                color: hover ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.88)",
                textDecoration: "underline",
                textDecorationColor: hover ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.55)",
                textDecorationThickness: "1px",
                textUnderlineOffset: "3px",
                transition: "color 0.15s ease, text-decoration-color 0.15s ease",
                letterSpacing: "0.01em",
                cursor: "pointer",
                whiteSpace: "nowrap",
            }}
        >
            {compact ? "PDF Deck" : "Shared with PDF Deck"}
        </a>
    );
};

export default Flipbook;
